import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaUpload, FaPaperPlane } from 'react-icons/fa';
import styles from '../styles_career/JobApplicationModal.module.css';

const JobApplicationModal = ({ isOpen, onClose, jobTitle }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    currentRole: '',
    cv: null,
    coverLetter: null,
    additionalDocuments: null
  });

  const [formErrors, setFormErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files[0]
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.fullName) errors.fullName = 'Full Name is required';
    if (!formData.email) errors.email = 'Email is required';
    if (!formData.phone) errors.phone = 'Phone number is required';
    if (!formData.cv) errors.cv = 'CV/Resume is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Here you would typically send the form data to a backend service
      // For now, we'll simulate a submission
      setSubmitStatus('submitting');
      
      setTimeout(() => {
        setSubmitStatus('success');
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setSubmitStatus(null);
          onClose();
        }, 3000);
      }, 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div 
        className={styles.modalOverlay}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div 
          className={styles.modalContent}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
        >
          <button className={styles.closeButton} onClick={onClose}>
            <FaTimes />
          </button>

          <h2>Apply for {jobTitle}</h2>
          
          {submitStatus === 'success' ? (
            <motion.div 
              className={styles.successMessage}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h3>Application Submitted Successfully!</h3>
              <p>We'll review your application and get back to you soon.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className={styles.applicationForm}>
              <div className={styles.formGroup}>
                <label>Full Name *</label>
                <input 
                  type="text" 
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                />
                {formErrors.fullName && <span className={styles.errorText}>{formErrors.fullName}</span>}
              </div>

              <div className={styles.formGroup}>
                <label>Email *</label>
                <input 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                />
                {formErrors.email && <span className={styles.errorText}>{formErrors.email}</span>}
              </div>

              <div className={styles.formGroup}>
                <label>Phone Number *</label>
                <input 
                  type="tel" 
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter your phone number"
                />
                {formErrors.phone && <span className={styles.errorText}>{formErrors.phone}</span>}
              </div>

              <div className={styles.formGroup}>
                <label>Current Role</label>
                <input 
                  type="text" 
                  name="currentRole"
                  value={formData.currentRole}
                  onChange={handleInputChange}
                  placeholder="Your current job title (optional)"
                />
              </div>

              <div className={styles.formGroup}>
                <label>CV/Resume * {formData.cv && <span>({formData.cv.name})</span>}</label>
                <div className={styles.fileUpload}>
                  <input 
                    type="file" 
                    name="cv"
                    id="cv"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="cv" className={styles.fileUploadLabel}>
                    <FaUpload /> Upload CV
                  </label>
                  {formErrors.cv && <span className={styles.errorText}>{formErrors.cv}</span>}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Cover Letter (Optional) {formData.coverLetter && <span>({formData.coverLetter.name})</span>}</label>
                <div className={styles.fileUpload}>
                  <input 
                    type="file" 
                    name="coverLetter"
                    id="coverLetter"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="coverLetter" className={styles.fileUploadLabel}>
                    <FaUpload /> Upload Cover Letter
                  </label>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Additional Documents (Optional) {formData.additionalDocuments && <span>({formData.additionalDocuments.name})</span>}</label>
                <div className={styles.fileUpload}>
                  <input 
                    type="file" 
                    name="additionalDocuments"
                    id="additionalDocuments"
                    accept=".pdf,.doc,.docx,.jpg,.png"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="additionalDocuments" className={styles.fileUploadLabel}>
                    <FaUpload /> Upload Additional Documents
                  </label>
                </div>
              </div>

              <button 
                type="submit" 
                className={styles.submitButton}
                disabled={submitStatus === 'submitting'}
              >
                {submitStatus === 'submitting' ? 'Submitting...' : 'Submit Application'}
                <FaPaperPlane />
              </button>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default JobApplicationModal;
